import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IRouteEventAlert, defaultValue } from 'app/shared/model/route-event-alert.model';
import { IInfoRoutesByTruckImplement, IInfoRoutesByCompany } from 'app/modules/dashboards/dashboard-truck-implement/interface/dashboardTruckImplementInterface';

const initialState: EntityState<IRouteEventAlert> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/route-event-alerts';

export const getRouteEventsByTruckAndDateRange = createAsyncThunk(
  'routeEventAlert/fetch_by_truck_date_range',
  async ({ truckImplementId, dateStartPeriodic, dateEndPeriodic, page, size, sort, virtualFenceId }: IInfoRoutesByTruckImplement) => {
    const routeParam = virtualFenceId != null ? `&routeId=${virtualFenceId}` : '';
    return axios.get<IRouteEventAlert[]>(
      `${apiUrl}/truck-implement/${truckImplementId}/${dateStartPeriodic}/${dateEndPeriodic}?page=${page}&size=${size}&sort=${sort}${routeParam}&cacheBuster=${new Date().getTime()}`
    );
  },
  { serializeError: serializeAxiosError }
);

export const getRouteEventsByTruckAndPeriodic = createAsyncThunk(
  'routeEventAlert/fetch_by_truck_periodic',
  async ({ truckImplementId, periodicType, page, size, sort, virtualFenceId }: IInfoRoutesByTruckImplement) => {
    const routeParam = virtualFenceId != null ? `&routeId=${virtualFenceId}` : '';
    return axios.get<IRouteEventAlert[]>(
      `${apiUrl}/truck-implement/${truckImplementId}/${periodicType}?page=${page}&size=${size}&sort=${sort}${routeParam}&cacheBuster=${new Date().getTime()}`
    );
  },
  { serializeError: serializeAxiosError }
);

export const getRouteEventsByCompanyAndDateRange = createAsyncThunk(
  'routeEventAlert/fetch_by_company_date_range',
  async ({ dateStartPeriodic, dateEndPeriodic, licensePlateSearch, page, size, sort, searchCompanyId, truckImplementId }: IInfoRoutesByCompany) => {
    return axios.get<IRouteEventAlert[]>(
      `${apiUrl}/company/${dateStartPeriodic}/${dateEndPeriodic}?page=${page}&size=${size}&sort=${sort}&licensePlateSearch=${
        licensePlateSearch ?? ''
      }&cacheBuster=${new Date().getTime()}`,
      { params: { searchCompanyId, truckImplementId } }
    );
  },
  { serializeError: serializeAxiosError }
);

export const getRouteEventsByCompanyAndPeriodic = createAsyncThunk(
  'routeEventAlert/fetch_by_company_periodic',
  async ({ periodicType, licensePlateSearch, page, size, sort, searchCompanyId, truckImplementId }: IInfoRoutesByCompany) => {
    return axios.get<IRouteEventAlert[]>(
      `${apiUrl}/company/${periodicType}?page=${page}&size=${size}&sort=${sort}&licensePlateSearch=${
        licensePlateSearch ?? ''
      }&cacheBuster=${new Date().getTime()}`,
      { params: { searchCompanyId, truckImplementId } }
    );
  },
  { serializeError: serializeAxiosError }
);

export const RouteEventAlertSlice = createEntitySlice({
  name: 'routeEventAlert',
  initialState,
  extraReducers(builder) {
    builder
      .addMatcher(
        isFulfilled(
          getRouteEventsByTruckAndDateRange,
          getRouteEventsByTruckAndPeriodic,
          getRouteEventsByCompanyAndDateRange,
          getRouteEventsByCompanyAndPeriodic
        ),
        (state, action) => {
          state.loading = false;
          state.entities = action.payload.data;
          state.totalItems = parseInt(action.payload.headers['x-total-count'], 10);
        }
      )
      .addMatcher(
        isPending(getRouteEventsByTruckAndDateRange, getRouteEventsByTruckAndPeriodic, getRouteEventsByCompanyAndDateRange, getRouteEventsByCompanyAndPeriodic),
        state => {
          state.errorMessage = null;
          state.updateSuccess = false;
          state.loading = true;
        }
      );
  },
});

export const { reset } = RouteEventAlertSlice.actions;

export default RouteEventAlertSlice.reducer;
